import { Component, OnInit } from '@angular/core';

import { AdministrationService } from './administration.service';
import {
  AdminUserData,
  AdminUserResponse,
  CreateAdminRequestData,
  CreateAdminResponse,
  DeleteAdminResponse,
  RolePermissionGroup,
} from './administration.domain';
import { ErrorService } from '../services/error/error.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LookupOptionsData } from '../jurisdiction/jurisdiction.domain';
import { AllDropdownKeys, TableConfig } from '../constants/app.constants';
import { CommonService } from '../services/common.service';
import { LocalStorageService } from '../services/local-storage.service';
import { TableConfiguration } from '../domains/common.domain';
import { ConfirmationService } from 'primeng/api';
import { ValidationsService } from '../services/validations.service';

@Component({
  providers: [AdministrationService, ConfirmationService],
  selector: 'app-administration',
  styleUrls: ['./administration.component.scss'],
  templateUrl: './administration.component.html',
})
export class AdministrationComponent implements OnInit {
  adminData: AdminUserData[] = [];
  adminFG: FormGroup;
  canChangePermissions: boolean = false;
  currentTechnicianId: string;
  filterFields: string[] = ['name', 'email', 'role'];
  globalFilterValue: string;
  isLoading: boolean;
  permissionsData: RolePermissionGroup[];
  roleTypes: LookupOptionsData[];
  roleTypesKey: string = AllDropdownKeys.AdminRole;
  showPassword: boolean = false;
  tableConfig: TableConfiguration = TableConfig;

  constructor(
    private administrationService: AdministrationService,
    private commonService: CommonService,
    private confirmationService: ConfirmationService,
    private errorService: ErrorService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.roleTypes = this.commonService.getDropdownData(this.roleTypesKey);
    this.currentTechnicianId = this.localStorageService.getItem('technician_id');

    this.prepareForm();

    this.getAdminUserData();
    this.getPermissionGroupData();
  }

  addAdminUser(): void {
    if (this.adminFG.valid) {
      const requestData: CreateAdminRequestData = {
        email: this.adminFG.value.email,
        password: this.adminFG.value.password,
        role: this.adminFG.value.role,
        first_name: this.adminFG.value.firstName,
        last_name: this.adminFG.value.lastName,
      };
      this.administrationService.createNewAdmin(requestData).subscribe(
        (response: CreateAdminResponse) => {
          this.errorService.throwErrorIfFailure(response, true);
          this.adminFG.reset();
          this.getAdminUserData();
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    }
  }

  deleteAdminUser(technicianId: string): void {
    this.confirmationService.confirm({
      accept: () => {
        this.confirmationService.close();
        this.administrationService.deleteAdminUser(technicianId).subscribe(
          (response: DeleteAdminResponse) => {
            this.errorService.throwErrorIfFailure(response, true);
            this.getAdminUserData();
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  showDeleteIcon(technicianData: AdminUserData): boolean {
    let isRoleMatch = false;

    ['employee', 'observer', 'administrator'].forEach(role => {
      isRoleMatch = isRoleMatch || technicianData.role === role;
    });

    return technicianData.technician_id !== this.currentTechnicianId && isRoleMatch;
  }

  private getAdminUserData(): void {
    this.isLoading = true;
    this.administrationService.getAllAdminUsers().subscribe(
      (response: AdminUserResponse) => {
        this.errorService.throwErrorIfFailure(response, true);
        this.adminData = response.adminuser.map(item => {
          item.name = `${item.first_name} ${item.last_name}`;
          return item;
        });
        this.isLoading = false;
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private prepareForm(): void {
    this.adminFG = new FormGroup({
      email: new FormControl('', [Validators.required, ValidationsService.emailValidator()]),
      password: new FormControl('', [Validators.required]),
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      role: new FormControl('', [Validators.required]),
    });
  }

  private getPermissionGroupData(): void {
    this.isLoading = true;

    this.administrationService.getPermissionGroupData().subscribe(
      response => {
        this.errorService.throwErrorIfFailure(response, true);
        this.permissionsData = response;
      },
      error => this.errorService.throwErrorIfFailure(error, true),
      () => (this.isLoading = false)
    );
  }
}
